/**
 * Evaluations React Query Hooks
 *
 * Custom hooks for evaluation management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  evaluationsApi,
  type EvaluationStatus,
  type CreateEvaluationInput,
  type SelfEvaluationInput,
  type TaskScore,
  type CoreTaskScore,
  type CoreTaskSelfEvaluationScore,
  type CompetencyScore,
  type AgreementInput,
} from '../../lib/evaluationsApi';

// Query keys
export const evaluationKeys = {
  all: ['evaluations'] as const,
  lists: () => [...evaluationKeys.all, 'list'] as const,
  list: (params?: { employeeId?: number; managerId?: number; status?: EvaluationStatus }) =>
    [...evaluationKeys.lists(), params] as const,
  details: () => [...evaluationKeys.all, 'detail'] as const,
  detail: (id: number) => [...evaluationKeys.details(), id] as const,
  selfEvaluation: (id: number) => [...evaluationKeys.detail(id), 'self-evaluation'] as const,
  coreTaskSelfEvaluation: (id: number) => [...evaluationKeys.detail(id), 'core-task-self-evaluation'] as const,
  wizard: (id: number) => [...evaluationKeys.detail(id), 'wizard'] as const,
  calculation: (id: number) => [...evaluationKeys.detail(id), 'calculation'] as const,
  agreements: (id: number) => [...evaluationKeys.detail(id), 'agreements'] as const,
};

/**
 * Hook to list evaluations
 */
export function useEvaluations(params?: {
  employeeId?: number;
  managerId?: number;
  status?: EvaluationStatus;
}) {
  return useQuery({
    queryKey: evaluationKeys.list(params),
    queryFn: () => evaluationsApi.list(params),
  });
}

/**
 * Hook to get evaluation by ID
 */
export function useEvaluation(id: number) {
  return useQuery({
    queryKey: evaluationKeys.detail(id),
    queryFn: () => evaluationsApi.getById(id),
    enabled: id > 0,
  });
}

/**
 * Hook to create evaluation
 */
export function useCreateEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEvaluationInput) => evaluationsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.lists() });
    },
  });
}

/**
 * Hook to update evaluation status
 */
export function useUpdateEvaluationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, opmerkingen }: { id: number; status: EvaluationStatus; opmerkingen?: string }) =>
      evaluationsApi.updateStatus(id, status, opmerkingen),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.lists() });
    },
  });
}

/**
 * Hook to delete evaluation
 */
export function useDeleteEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => evaluationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.lists() });
    },
  });
}

// Self-evaluation hooks

/**
 * Hook to get self-evaluation
 */
export function useSelfEvaluation(evaluationId: number) {
  return useQuery({
    queryKey: evaluationKeys.selfEvaluation(evaluationId),
    queryFn: () => evaluationsApi.getSelfEvaluation(evaluationId),
    enabled: evaluationId > 0,
  });
}

/**
 * Hook to save self-evaluation
 */
export function useSaveSelfEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, tasks }: { evaluationId: number; tasks: SelfEvaluationInput[] }) =>
      evaluationsApi.saveSelfEvaluation(evaluationId, tasks),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.selfEvaluation(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

/**
 * Hook to submit self-evaluation
 */
export function useSubmitSelfEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluationId: number) => evaluationsApi.submitSelfEvaluation(evaluationId),
    onSuccess: (_, evaluationId) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.selfEvaluation(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.lists() });
    },
  });
}

// Epic 4 — Beoordelen op kerntaken (Story 4.5, AC #1): core_tasks_v2 zelfevaluatie-hooks

export function useCoreTaskSelfEvaluation(evaluationId: number) {
  return useQuery({
    queryKey: evaluationKeys.coreTaskSelfEvaluation(evaluationId),
    queryFn: () => evaluationsApi.getCoreTaskSelfEvaluation(evaluationId),
    enabled: evaluationId > 0,
  });
}

export function useSaveCoreTaskSelfEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, scores }: { evaluationId: number; scores: CoreTaskSelfEvaluationScore[] }) =>
      evaluationsApi.saveCoreTaskSelfEvaluation(evaluationId, scores),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.coreTaskSelfEvaluation(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

export function useSubmitCoreTaskSelfEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (evaluationId: number) => evaluationsApi.submitCoreTaskSelfEvaluation(evaluationId),
    onSuccess: (_, evaluationId) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.coreTaskSelfEvaluation(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.lists() });
    },
  });
}

// Wizard hooks

/**
 * Hook to get wizard state
 */
export function useWizardState(evaluationId: number) {
  return useQuery({
    queryKey: evaluationKeys.wizard(evaluationId),
    queryFn: () => evaluationsApi.getWizardState(evaluationId),
    enabled: evaluationId > 0,
  });
}

/**
 * Hook to save wizard state
 */
export function useSaveWizardState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      evaluationId,
      currentStep,
      stepData,
    }: {
      evaluationId: number;
      currentStep: number;
      stepData: Record<string, unknown>;
    }) => evaluationsApi.saveWizardState(evaluationId, currentStep, stepData),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.wizard(evaluationId) });
    },
  });
}

// Scoring hooks

/**
 * Hook to save task scores
 */
export function useSaveTaskScores() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, scores }: { evaluationId: number; scores: TaskScore[] }) =>
      evaluationsApi.saveTaskScores(evaluationId, scores),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.calculation(evaluationId) });
    },
  });
}

/**
 * Hook to save core-task scores (Epic 4 — Beoordelen op kerntaken, Story 4.2, core_tasks_v2)
 */
export function useSaveCoreTaskScores() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, scores }: { evaluationId: number; scores: CoreTaskScore[] }) =>
      evaluationsApi.saveCoreTaskScores(evaluationId, scores),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.calculation(evaluationId) });
    },
  });
}

/**
 * Hook to save competency scores
 */
export function useSaveCompetencyScores() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, scores }: { evaluationId: number; scores: CompetencyScore[] }) =>
      evaluationsApi.saveCompetencyScores(evaluationId, scores),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.calculation(evaluationId) });
    },
  });
}

/**
 * Hook to calculate scores
 */
export function useCalculateScores(evaluationId: number) {
  return useQuery({
    queryKey: evaluationKeys.calculation(evaluationId),
    queryFn: () => evaluationsApi.calculateScores(evaluationId),
    enabled: evaluationId > 0,
  });
}

// Agreement hooks

/**
 * Hook to get agreements
 */
export function useAgreements(evaluationId: number) {
  return useQuery({
    queryKey: evaluationKeys.agreements(evaluationId),
    queryFn: () => evaluationsApi.getAgreements(evaluationId),
    enabled: evaluationId > 0,
  });
}

/**
 * Hook to add agreement
 */
export function useAddAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, input }: { evaluationId: number; input: AgreementInput }) =>
      evaluationsApi.addAgreement(evaluationId, input),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.agreements(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

/**
 * Hook to update agreement
 */
export function useUpdateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      evaluationId,
      agreementId,
      input,
    }: {
      evaluationId: number;
      agreementId: number;
      input: Partial<AgreementInput & { isCompleted: boolean }>;
    }) => evaluationsApi.updateAgreement(evaluationId, agreementId, input),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.agreements(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}

/**
 * Hook to delete agreement
 */
export function useDeleteAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ evaluationId, agreementId }: { evaluationId: number; agreementId: number }) =>
      evaluationsApi.deleteAgreement(evaluationId, agreementId),
    onSuccess: (_, { evaluationId }) => {
      queryClient.invalidateQueries({ queryKey: evaluationKeys.agreements(evaluationId) });
      queryClient.invalidateQueries({ queryKey: evaluationKeys.detail(evaluationId) });
    },
  });
}
